import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { createContext, useCallback, useEffect, useState } from 'react';

export type GitHubConnectionState = 'checking' | 'connected' | 'none';

export type GitHubConnection = {
  state: GitHubConnectionState;
  /** Opens the GitHub sign-in popup; resolves once the user is connected or gave up. */
  connect: () => Promise<void>;
  connecting: boolean;
  /** Why the last `connect` failed, for display. */
  error: string;
};

/**
 * Whether the signed-in user has connected their company GitHub account with
 * the `repo` scope, which the portal needs to read and act on GitHub as them.
 * Re-checks whenever the session changes, e.g. after the sign-in popup or a
 * disconnect on the settings page. Never opens a prompt by itself.
 */
export function useGitHubConnection(): GitHubConnection {
  const githubAuth = useApi(githubAuthApiRef);
  const [state, setState] = useState<GitHubConnectionState>('checking');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const subscription = githubAuth.sessionState$().subscribe(() => {
      githubAuth
        .getAccessToken(['repo'], { optional: true })
        .then(token => active && setState(token ? 'connected' : 'none'))
        .catch(() => active && setState('none'));
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [githubAuth]);

  const connect = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      // The user asked to connect, so open the GitHub popup straight away
      // instead of Backstage's "Login Required" dialog.
      const token = await githubAuth.getAccessToken(['repo'], {
        instantPopup: true,
      });
      setState(token ? 'connected' : 'none');
    } catch {
      setError('GitHub sign-in was cancelled or failed. Try again.');
    } finally {
      setConnecting(false);
    }
  }, [githubAuth]);

  return { state, connect, connecting, error };
}

/**
 * The connection a `GitHubConnectionGate` already tracks, so a banner inside
 * its preview connects through the same state and the gate swaps in the page
 * as soon as the popup succeeds.
 */
export const GitHubConnectionContext = createContext<
  GitHubConnection | undefined
>(undefined);
